import styled from 'styled-components'
import { COLOR } from '@farewill/ui/tokens'

import SelectInput from 'components/form/select-input'
import { Option } from 'components/form/select-input/types'
import { formatDate } from 'lib/formatting/dates'
import { ProbateEstatePerson } from 'lib/models/probate-estate'

import { fullName, hasBothNames, NEW_PERSON } from './helpers'

const StyledDateOfBirth = styled.span`
  flex-shrink: 0;
  color: ${COLOR.GREY.MEDIUM};
  white-space: nowrap;
`

const nameKey = (person: ProbateEstatePerson) =>
  fullName(person.attributes).trim().toLowerCase().replace(/\s+/g, ' ')

/**
 * Everyone who could fill this card: someone new, or a contact already on
 * the estate who isn't in this section yet. When people in the same family
 * share a name, their dates of birth tell them apart.
 */
export const contactOptions = ({
  people,
  memberIds,
  currentId,
  isDraft,
}: {
  people: ProbateEstatePerson[]
  memberIds: number[]
  currentId?: number
  isDraft: boolean
}): Option[] => {
  const names = people.map(nameKey)
  const sharesName = (person: ProbateEstatePerson) =>
    names.filter((name) => name === nameKey(person)).length > 1

  return [
    {
      label: isDraft ? 'New person (unsaved)' : 'New person',
      value: NEW_PERSON,
    },
    ...people
      .filter(
        (person) =>
          person.id === currentId ||
          (!memberIds.includes(person.id) && hasBothNames(person.attributes))
      )
      .map((person) => ({
        label: fullName(person.attributes) || 'No name given',
        value: person.id,
        ...(sharesName(person) && {
          suffix: (
            <StyledDateOfBirth>
              {formatDate(person.attributes.dateOfBirth) ?? 'No date of birth'}
            </StyledDateOfBirth>
          ),
        }),
      })),
  ]
}

type Props = {
  options: Option[]
  onChoose: (value: string | number) => unknown
  hideSelectedDateOfBirth?: boolean
}

const ContactSelect = ({
  options,
  onChoose,
  hideSelectedDateOfBirth = false,
}: Props) => (
  <SelectInput
    floating
    name="contact"
    label="Contact"
    options={options}
    hideSelectedSuffix={hideSelectedDateOfBirth}
    onChange={(selected) => {
      if (selected && 'value' in selected) onChoose(selected.value)
    }}
  />
)

export default ContactSelect
